import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Activity, Wifi, WifiOff, AlertTriangle, ScanLine, Zap, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useEventMeta } from "@/lib/event-meta";
import { API_BASE } from "@/lib/utils";
import { formatDualClock, useSyncedClock } from "@/lib/synced-clock";
import { timezoneLabel } from "@/lib/timezone";

const CHECKIN_QUEUE_PREFIX = "aivory.checkin.queue.";

function loadCheckinQueue(eventId) {
  try {
    const raw = localStorage.getItem(`${CHECKIN_QUEUE_PREFIX}${eventId}`);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveCheckinQueue(eventId, items) {
  try {
    localStorage.setItem(`${CHECKIN_QUEUE_PREFIX}${eventId}`, JSON.stringify(items.slice(0, 50)));
  } catch {
    /* storage full or private mode */
  }
}

export default function LiveCheckin() {
  const { id } = useParams();
  const e = useEventMeta(id);
  const [count, setCount] = useState(0);
  const [registered, setRegistered] = useState(0);
  const [invited, setInvited] = useState(0);
  const [invitees, setInvitees] = useState(0);
  const [pendingApproval, setPendingApproval] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [feed, setFeed] = useState([]);
  const [throughputSeries, setThroughputSeries] = useState([]);
  const [gateThroughput, setGateThroughput] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [deviceHealth, setDeviceHealth] = useState({ online: 0, offline: 0, slots: [] });
  const [sessionOccupancy, setSessionOccupancy] = useState([]);
  const [currentIso, setCurrentIso] = useState("");
  const [scanCode, setScanCode] = useState("");
  const [gate, setGate] = useState("Gate A");
  const [staff, setStaff] = useState([]);
  const [operatorId, setOperatorId] = useState("");
  const [operatorPin, setOperatorPin] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [displayTimeZone, setDisplayTimeZone] = useState("Asia/Singapore");
  const [networkOnline, setNetworkOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true,
  );
  const [pendingQueue, setPendingQueue] = useState(() => loadCheckinQueue(id));
  const [dashboardError, setDashboardError] = useState(false);
  const [resetting, setResetting] = useState(false);
  const safeThroughput = Array.isArray(throughputSeries) ? throughputSeries : [];
  const syncedNow = useSyncedClock(currentIso);

  const applyDashboard = (d) => {
    if (!d || typeof d !== "object") return;
    setCount(Number(d.checkedIn) || 0);
    setRegistered(Number(d.registered) || 0);
    setInvited(Number(d.invited) || 0);
    setInvitees(
      Number(d.invitees) || (Number(d.registered) || 0) + (Number(d.invited) || 0),
    );
    setPendingApproval(Number(d.pendingApproval) || 0);
    setRemaining(
      Number(d.remaining)
        ?? Math.max(0, (Number(d.invitees) || (Number(d.registered) || 0) + (Number(d.invited) || 0)) - (Number(d.checkedIn) || 0)),
    );
    setFeed(Array.isArray(d.feed) ? d.feed.slice(0, 8) : []);
    setThroughputSeries(Array.isArray(d.throughputSeries) ? d.throughputSeries : []);
    setGateThroughput(Array.isArray(d.gateThroughput) ? d.gateThroughput : []);
    setAlerts(Array.isArray(d.alerts) ? d.alerts : []);
    setDeviceHealth(d.deviceHealth || { online: 0, offline: 0, slots: [] });
    setSessionOccupancy(Array.isArray(d.sessionOccupancy) ? d.sessionOccupancy : []);
    if (d.serverTime) setCurrentIso(d.serverTime);
    if (d.timeZone) setDisplayTimeZone(String(d.timeZone));
  };

  useEffect(() => {
    const onOnline = () => setNetworkOnline(true);
    const onOffline = () => setNetworkOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  useEffect(() => {
    setPendingQueue(loadCheckinQueue(id));
  }, [id]);

  const flushQueue = async () => {
    const queue = loadCheckinQueue(id);
    if (!queue.length || !networkOnline) return;
    let remaining = [...queue];
    let synced = 0;
    for (const item of queue) {
      try {
        const res = await fetch(`${API_BASE}/api/events/${id}/${item.path}`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(item.body),
        });
        if (res.status >= 500) break;
        if (!res.ok) {
          if (res.status === 409) {
            toast.message("Already checked in (queued scan)");
          }
          remaining = remaining.filter((q) => q.id !== item.id);
          synced += 1;
          continue;
        }
        remaining = remaining.filter((q) => q.id !== item.id);
        synced += 1;
      } catch {
        break;
      }
    }
    saveCheckinQueue(id, remaining);
    setPendingQueue(remaining);
    if (synced > 0) {
      toast.success(`Synced ${synced} queued check-in(s)`);
      try {
        const dashRes = await fetch(`${API_BASE}/api/events/${id}/live-checkin-dashboard`);
        if (dashRes.ok) applyDashboard(await dashRes.json());
      } catch {
        /* keep last dashboard */
      }
    }
  };

  useEffect(() => {
    if (networkOnline && pendingQueue.length) {
      flushQueue();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkOnline, id, pendingQueue.length]);

  useEffect(() => {
    let cancel = false;
    const loadDashboard = async () => {
      try {
        const [staffRes, dashboardRes] = await Promise.all([
          fetch(`${API_BASE}/api/events/${id}/staff`),
          fetch(`${API_BASE}/api/events/${id}/live-checkin-dashboard`),
        ]);
        if (staffRes.ok && !cancel) {
          const d = await staffRes.json();
          setStaff(Array.isArray(d.staff) ? d.staff : []);
        }
        if (dashboardRes.ok && !cancel) {
          const d = await dashboardRes.json();
          applyDashboard(d);
          setDashboardError(false);
        } else if (!cancel) {
          setDashboardError(true);
        }
      } catch {
        if (!cancel) setDashboardError(true);
      }
    };
    loadDashboard();
    const i = setInterval(loadDashboard, 3500);
    return () => {
      cancel = true;
      clearInterval(i);
    };
  }, [id]);

  function isBadgeQrCode(value) {
    return String(value || "").startsWith("B1|");
  }

  const buildCheckinBody = (code, gateName, operatorStaffId, operatorStaffPin) => {
    const scannedAt = new Date().toISOString();
    const body = isBadgeQrCode(code)
      ? { code, gate: gateName, scanned_at: scannedAt }
      : code.includes("@")
        ? { email: code, gate: gateName, scanned_at: scannedAt }
        : { attendee_id: code, gate: gateName, scanned_at: scannedAt };
    if (operatorStaffId) {
      body.staff_id = operatorStaffId;
      if (operatorStaffPin) body.staff_pin = operatorStaffPin;
    }
    return body;
  };

  const queueScan = (path, body) => {
    const queueItem = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      path,
      body: { ...body, scanned_at: body.scanned_at || new Date().toISOString() },
      queuedAt: new Date().toISOString(),
    };
    const next = [...loadCheckinQueue(id), queueItem];
    saveCheckinQueue(id, next);
    setPendingQueue(next);
    return next.length;
  };

  const submitScan = async () => {
    const code = scanCode.trim();
    if (!code) return;
    const path = isBadgeQrCode(code) ? "badge/scan" : "checkin";
    const body = buildCheckinBody(code, gate, operatorId, operatorPin);
    if (!networkOnline) {
      const len = queueScan(path, body);
      setScanCode("");
      setOperatorPin("");
      toast.message("Offline — check-in queued", {
        description: `${len} scan(s) will sync when connectivity returns.`,
      });
      return;
    }
    try {
      setSubmitting(true);
      const res = await fetch(`${API_BASE}/api/events/${id}/${path}`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || res.statusText || "scan failed");
      }
      const payload = await res.json();
      setScanCode("");
      setOperatorPin("");
      const alreadyIn = Boolean(payload?.alreadyCheckedIn);
      toast.message(
        alreadyIn
          ? `${payload?.attendee?.name || "Attendee"} is already checked in`
          : `Checked in ${payload?.attendee?.name || "attendee"}${
              payload?.attendee?.seat ? ` · Seat ${payload.attendee.seat}` : ""
            }`,
      );
      try {
        const dashRes = await fetch(`${API_BASE}/api/events/${id}/live-checkin-dashboard`);
        if (dashRes.ok) applyDashboard(await dashRes.json());
      } catch {
        setFeed(Array.isArray(payload.feed) ? payload.feed.slice(0, 8) : []);
        setCount(Number(payload.checkedIn) || 0);
      }
    } catch (err) {
      const len = queueScan(path, body);
      toast.error("Check-in failed — queued for retry", {
        description: err.message || `${len} scan(s) queued for sync.`,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const resetAllCheckins = async () => {
    if (!id) return;
    const ok = window.confirm(
      "Reset all check-ins for this event? Checked-in attendees will return to their prior status and the live feed will clear.",
    );
    if (!ok) return;
    setResetting(true);
    try {
      const res = await fetch(`${API_BASE}/api/events/${id}/checkin/reset`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || res.statusText || "reset failed");
      }
      const payload = await res.json();
      toast.success(`Reset ${payload.reset || 0} check-in(s)`);
      const dashRes = await fetch(`${API_BASE}/api/events/${id}/live-checkin-dashboard`);
      if (dashRes.ok) applyDashboard(await dashRes.json());
      else {
        setCount(0);
        setFeed([]);
        setRemaining(invitees);
      }
    } catch (err) {
      toast.error(err.message || "Could not reset check-ins");
    } finally {
      setResetting(false);
    }
  };

  const peak = Math.max(...safeThroughput.map((d) => Number(d?.v || 0)), 1);
  const throughputNow = Number(safeThroughput[safeThroughput.length - 1]?.v || 0);
  const throughputAxis = useMemo(() => {
    if (!safeThroughput.length) return [];
    const last = safeThroughput.length - 1;
    const picks = [0, Math.floor(last / 4), Math.floor(last / 2), Math.floor((3 * last) / 4), last];
    return [...new Set(picks)].map((i) => safeThroughput[i]?.hr || "");
  }, [safeThroughput]);
  const clockLabel = formatDualClock(syncedNow, displayTimeZone);
  const tzShort = timezoneLabel(displayTimeZone);
  const gateScanUrl = `/onsite/scan?event=${encodeURIComponent(id)}&gate=${encodeURIComponent(gate)}`;
  const progressWidth = useMemo(() => {
    if (!invitees) return 0;
    return Math.min(100, Math.round((count / invitees) * 100));
  }, [count, invitees]);
  const arrivalRate = useMemo(() => {
    if (!invitees) return "0%";
    return `${Math.round((count / invitees) * 1000) / 10}%`;
  }, [count, invitees]);
  const noShowEst = useMemo(() => {
    if (!invitees) return "0%";
    return `${Math.round(((invitees - count) / invitees) * 1000) / 10}%`;
  }, [count, invitees]);

  return (
    <div className="live-checkin h-full min-h-0 overflow-y-auto" style={{ background: "var(--bg-base)" }} data-testid="live-checkin">
      {/* Top status bar */}
      <div className="border-b border-[var(--border-default)] px-4 md:px-8 py-3 flex flex-wrap items-center justify-between gap-3 text-[11px] font-mono uppercase tracking-widest">
        <div className="flex flex-wrap items-center gap-4 md:gap-6">
          <span className="flex items-center gap-2 text-[var(--primary)]"><span className="w-1.5 h-1.5 bg-[var(--primary)] rounded-full pulse-dot" /> LIVE</span>
          <span className="text-[var(--text-tertiary)] max-w-[200px] md:max-w-none truncate">{e.title}</span>
          {e.venue ? <span className="text-[var(--text-tertiary)] hidden lg:inline">{e.venue}</span> : null}
        </div>
        <div className="flex flex-wrap items-center gap-4 md:gap-6 text-[var(--text-secondary)]">
          <span className={`flex items-center gap-1.5 ${networkOnline ? "text-[var(--primary)]" : "text-[#E25D3A]"}`}>
            {networkOnline ? <Wifi size={11} /> : <WifiOff size={11} />}
            {networkOnline ? "ONLINE" : "OFFLINE"}
          </span>
          {pendingQueue.length ? (
            <span className="flex items-center gap-1.5 text-[#E25D3A]">
              <AlertTriangle size={11} /> {pendingQueue.length} QUEUED
            </span>
          ) : null}
          <span className="flex items-center gap-1.5"><Activity size={11} /> {throughputNow}/HR</span>
          <span className="hidden sm:flex items-center gap-1.5"><Wifi size={11} className="text-[var(--primary)]" /> {deviceHealth.online} DEVICES</span>
          <span className="hidden sm:flex items-center gap-1.5"><WifiOff size={11} className="text-[var(--text-tertiary)]" /> {deviceHealth.offline} OFFLINE</span>
          <span className="hidden md:inline" title="Event timezone (synced to server). Local time shown when different.">{clockLabel}</span>
        </div>
      </div>

      {!networkOnline || dashboardError || pendingQueue.length ? (
        <div className="mx-4 md:mx-8 mt-4 surface p-4 border border-[#E25D3A]/35 flex flex-wrap items-center gap-3 text-sm">
          <AlertTriangle size={16} className="text-[#E25D3A] shrink-0" />
          <div className="text-[var(--text-secondary)]">
            {!networkOnline ? (
              <span>Network offline — dashboard may be stale. Check-ins are queued locally.</span>
            ) : dashboardError ? (
              <span>Could not refresh live dashboard — showing last known counts.</span>
            ) : (
              <span>{pendingQueue.length} check-in(s) waiting to sync.</span>
            )}
          </div>
          {networkOnline && pendingQueue.length ? (
            <button type="button" className="btn-secondary text-xs ml-auto" onClick={flushQueue}>
              Sync queue now
            </button>
          ) : null}
        </div>
      ) : null}

      {/* Headline counters */}
      <div className="px-4 md:px-8 py-6 md:py-10 grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8 items-end">
        <div className="lg:col-span-7">
          <div className="text-overline">CHECKED-IN · CUMULATIVE</div>
          <div className="font-mono text-[clamp(3rem,12vw,120px)] leading-[0.9] tracking-tighter mt-2 text-[var(--text-primary)]">{count.toLocaleString()}</div>
          <div className="text-sm font-mono text-[var(--text-tertiary)] mt-2">
            of {invitees.toLocaleString()} invitees
            {invited > 0 ? (
              <span className="text-[var(--text-secondary)]">
                {" "}
                · {registered.toLocaleString()} registered · {invited.toLocaleString()} invited only
              </span>
            ) : null}
          </div>
          <div className="mt-4 h-1.5 bg-[var(--lc-panel-track)] max-w-md rounded-full overflow-hidden">
            <div className="h-full bg-[var(--primary)] transition-all duration-500" style={{ width: `${progressWidth}%` }} />
          </div>
          <div className="mt-2 text-[11px] font-mono text-[var(--text-tertiary)]">{arrivalRate} arrival rate</div>
          {count > 0 ? (
            <button
              type="button"
              className="btn-secondary text-xs mt-4 text-[var(--error)] border-[color-mix(in_srgb,var(--error)_35%,transparent)]"
              onClick={resetAllCheckins}
              disabled={resetting}
              data-testid="reset-all-checkins"
            >
              {resetting ? "Resetting…" : "Reset all check-ins"}
            </button>
          ) : null}
        </div>
        <div className="lg:col-span-5 grid grid-cols-2 gap-3">
          <Counter k="Invitees" v={invitees.toLocaleString()} hint="Guest list expected at gate" />
          <Counter k="Registered" v={registered.toLocaleString()} hint="Paid · registered · pending approval" />
          <Counter k="Invited" v={invited.toLocaleString()} hint="On guest list, not yet registered" />
          <Counter k="Remaining" v={remaining.toLocaleString()} hint="Invitees not yet checked in" />
          <Counter k="Throughput / hr" v={`${throughputNow}`} accent />
          <Counter k="Pending approval" v={pendingApproval.toLocaleString()} />
          <Counter k="No-show est." v={noShowEst} />
        </div>
      </div>

      <div className="px-4 md:px-8 pb-6 md:pb-8 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="surface p-4 flex flex-wrap items-center gap-3 border border-[color-mix(in_srgb,var(--primary)_25%,transparent)]">
            <div className="flex-1 min-w-[200px]">
              <div className="text-overline">Gate usher scan desk</div>
              <p className="text-sm text-[var(--text-secondary)] mt-1">
                Open on phones or tablets at each gate. Ushers scan badge QR codes with the device camera.
              </p>
            </div>
            <Link to={gateScanUrl} className="btn-primary shrink-0" target="_blank" rel="noopener noreferrer">
              <ExternalLink size={14} /> Open gate scan desk
            </Link>
          </div>
          <div className="surface p-4 flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[200px]">
              <div className="text-overline">Seat assignments</div>
              <p className="text-sm text-[var(--text-secondary)] mt-1">
                Seat numbers appear in the scan feed and on the usher screen when guests check in.
              </p>
            </div>
            <Link to={`/admin/events/${id}/seats`} className="btn-secondary shrink-0">
              <ExternalLink size={14} /> Seat arrangement
            </Link>
          </div>
        </div>
        <div className="surface p-4">
          <div className="text-overline mb-3">Manual check-in</div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              value={scanCode}
              onChange={(ev) => setScanCode(ev.target.value)}
              onKeyDown={(ev) => {
                if (ev.key === "Enter") submitScan();
              }}
              placeholder="Attendee id, email, or badge QR (B1|...)"
              className="input-dark flex-1 min-w-[220px] font-mono text-xs"
            />
            <select value={gate} onChange={(ev) => setGate(ev.target.value)} className="input-dark max-w-[140px]">
              <option>Gate A</option>
              <option>Gate B</option>
              <option>Gate C</option>
            </select>
            <select
              value={operatorId}
              onChange={(ev) => {
                setOperatorId(ev.target.value);
                setOperatorPin("");
              }}
              className="input-dark max-w-[200px] text-xs"
              title="Identify operator for audit log (PIN required if set on staff profile)"
            >
              <option value="">Operator (optional)</option>
              {staff.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.badge_label || s.name} · {s.role_type}
                  {s.has_pin ? " · PIN" : ""}
                </option>
              ))}
            </select>
            {operatorId && staff.find((s) => s.id === operatorId)?.has_pin ? (
              <input
                type="password"
                value={operatorPin}
                onChange={(ev) => setOperatorPin(ev.target.value)}
                placeholder="Operator PIN"
                className="input-dark max-w-[120px] font-mono text-xs"
              />
            ) : null}
            <button type="button" onClick={submitScan} disabled={submitting} className="btn-primary">
              <ScanLine size={14} /> {submitting ? "Checking..." : "Check in"}
            </button>
          </div>
          <p className="text-[11px] text-[var(--text-tertiary)] font-mono mt-3">
            Supports attendee id, email, and badge QR. Operators are managed under Attendees → Staff &amp; badges.
          </p>
        </div>
      </div>

      {/* Mid: throughput + alerts/heatmap */}
      <div className="px-4 md:px-8 pb-6 grid grid-cols-1 md:grid-cols-12 gap-4">
        <div className="md:col-span-8 lc-panel p-4 md:p-8 relative overflow-hidden">
          <div className="lc-section-label mb-3 flex items-center gap-2 flex-wrap">
            <Zap size={11} className="lc-accent" />
            <span>THROUGHPUT — last 24 hrs</span>
            <span className="normal-case tracking-normal text-[10px] lc-meta">({tzShort})</span>
          </div>
          <svg viewBox="0 0 1000 220" className="w-full h-[220px]">
            <defs>
              <linearGradient id="th" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.5" />
                <stop offset="100%" stopColor="var(--primary)" stopOpacity="0" />
              </linearGradient>
            </defs>
            {[0.25, 0.5, 0.75].map((p) => (
              <line key={p} x1="0" y1={220 * p} x2="1000" y2={220 * p} stroke="var(--border-default)" strokeDasharray="2 4" />
            ))}
            {safeThroughput.map((d, i) => {
              const denom = Math.max(1, safeThroughput.length - 1);
              const x = (i / denom) * 1000;
              const h = (Number(d?.v || 0) / peak) * 200;
              return <rect key={i} x={x - 12} y={220 - h} width="24" height={h} fill={d.v === peak ? "var(--primary)" : "color-mix(in srgb, var(--primary) 42%, transparent)"} />;
            })}
          </svg>
          <div className="flex justify-between text-[11px] font-mono lc-meta mt-2 tabular-nums">
            {throughputAxis.length ? throughputAxis.map((label) => <span key={label}>{label}</span>) : (
              <>
                <span>—</span>
              </>
            )}
          </div>
        </div>

        <div className="md:col-span-4 lc-panel p-6 md:p-8">
          <div className="lc-section-label mb-4 flex items-center gap-2"><AlertTriangle size={11} className="lc-accent" /> ALERTS</div>
          <div className="space-y-3">
            {alerts.map((a, i) => (
              <Alert key={`${a.title}-${i}`} color={a.color || "var(--primary)"} icon={AlertTriangle} title={a.title} body={a.body} time={a.time} />
            ))}
            {!alerts.length ? <div className="text-xs text-[var(--text-tertiary)] font-mono">No active alerts.</div> : null}
          </div>
        </div>
      </div>

      {/* Bottom: feed + gates + heatmap */}
      <div className="px-4 md:px-8 pb-8 grid grid-cols-1 md:grid-cols-12 gap-4">
        <div className="md:col-span-5 lc-panel p-6 md:p-8 max-h-[420px] overflow-hidden">
          <div className="lc-section-label mb-4 flex items-center gap-2"><ScanLine size={11} className="lc-accent" /> LIVE SCAN FEED</div>
          <div className="space-y-2">
            {feed.map((f, i) => (
              <div key={`${f.time}-${f.name}-${i}`} className="flex items-center gap-4 py-2 border-b border-dashed border-[var(--border-default)] fade-up" style={{ animationDelay: `${i * 30}ms` }}>
                <span className="font-mono text-[11px] lc-accent w-20 shrink-0 font-medium tabular-nums">{f.time}</span>
                <span className="w-1.5 h-1.5 bg-[var(--primary)] rounded-full shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate text-[var(--text-primary)]">{f.name || "Attendee"}</div>
                  <div className="text-xs lc-meta font-mono truncate">
                    {f.company || "—"}
                    {f.seat ? ` · Seat ${f.seat}` : ""}
                  </div>
                </div>
                <span className="font-mono text-[11px] text-[var(--text-primary)] uppercase shrink-0 font-medium">{f.gate}</span>
                <span className="font-mono text-[11px] uppercase tracking-widest px-2 py-0.5 border border-[color-mix(in_srgb,var(--primary)_50%,var(--border-default))] lc-accent font-medium shrink-0 max-w-[100px] truncate">{f.ticket}</span>
              </div>
            ))}
            {!feed.length ? (
              <div className="text-xs lc-meta font-mono py-6 text-center">No check-ins in the last 24 hours.</div>
            ) : null}
          </div>
        </div>

        <div className="md:col-span-3 lc-panel p-6 md:p-8">
          <div className="lc-section-label mb-4">GATE THROUGHPUT</div>
          <div className="space-y-5">
            {gateThroughput.map((row) => (
              <div key={row.gate}>
                <div className="flex justify-between font-mono text-xs items-baseline gap-3">
                  <span className="text-[var(--text-primary)] font-medium">{row.gate}</span>
                  <span className="text-[var(--text-primary)] font-semibold tabular-nums shrink-0">{row.perHour}/hr</span>
                </div>
                <div className="mt-2 h-1.5 bg-[var(--lc-panel-track)] rounded-sm overflow-hidden">
                  <div className="h-full rounded-sm" style={{ width: `${row.pct}%`, background: row.color || "var(--primary)" }} />
                </div>
              </div>
            ))}
            {!gateThroughput.length ? <div className="text-xs lc-meta">No gate scans yet.</div> : null}
          </div>

          <div className="lc-section-label mt-10 mb-3">DEVICE HEALTH</div>
          <div className="grid grid-cols-4 gap-1.5">
            {(deviceHealth.slots || []).map((slot, i) => (
              <div
                key={i}
                className="aspect-square rounded-sm"
                style={{ background: slot.online ? "var(--primary)" : "#52525B" }}
                title={slot.label || `Device ${i + 1}`}
              />
            ))}
          </div>
        </div>

        <div className="md:col-span-4 lc-panel p-6 md:p-8">
          <div className="lc-section-label mb-4 flex flex-wrap items-center gap-2">
            <span>SESSION OCCUPANCY · NEXT 2 HRS</span>
            <span className="normal-case tracking-normal text-[10px] lc-meta">({tzShort})</span>
          </div>
          <div className="space-y-4">
            {(sessionOccupancy || []).map((row) => (
              <div key={row.id || `${row.title}-${row.time}`}>
                <div className="flex justify-between text-xs gap-3">
                  <span className="text-[var(--text-primary)] font-medium truncate">{row.title}</span>
                  <span className="font-mono lc-meta shrink-0 tabular-nums">{row.time}</span>
                </div>
                <div className="mt-2 h-2 bg-[var(--lc-panel-track)] flex rounded-sm overflow-hidden">
                  {Array.from({ length: 30 }).map((_, i) => (
                    <div key={i} className="flex-1 mr-px" style={{ background: i / 30 < Number(row.occupancyRatio || 0) ? (Number(row.occupancyRatio || 0)  > 0.95 ? "#E25D3A" : "var(--primary)") : "transparent" }} />
                  ))}
                </div>
                <div className="text-[11px] font-mono lc-meta mt-1.5">{row.checkedInEstimate} est. in-room / {row.capacity} capacity</div>
              </div>
            ))}
            {!sessionOccupancy.length ? <div className="text-xs lc-meta">No sessions in the next 2 hours.</div> : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function Counter({ k, v, accent, hint }) {
  return (
    <div className="lc-panel p-5 md:p-6">
      <div className="lc-section-label">{k}</div>
      <div className={`font-mono text-3xl md:text-4xl mt-2 ${accent ? "lc-accent" : "text-[var(--text-primary)]"}`}>{v}</div>
      {hint ? <div className="text-[11px] lc-meta font-mono mt-2 leading-snug">{hint}</div> : null}
    </div>
  );
}

function Alert({ color, icon: Icon, title, body, time }) {
  return (
    <div className="surface p-3 flex items-center gap-3">
      <Icon size={14} style={{ color }} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-[var(--text-primary)]">{title}</div>
        <div className="text-xs lc-meta font-mono truncate">{body}</div>
      </div>
      <div className="text-[11px] font-mono lc-meta uppercase tracking-widest shrink-0">{time}</div>
    </div>
  );
}
